/**
 * Patients Service
 * API calls for patient profile operations
 */

import { API_ENDPOINTS } from '@/constants/api';
import { apiClient, ApiResponse } from './apiClient';

export interface PatientDTO {
  id: string;
  userId: string;
  phone?: string;
  cpf?: string;
  dateOfBirth?: string;
  gender?: 'M' | 'F' | 'Outro';
  /** Naturalidade (cidade/UF de nascimento) */
  birthPlace?: string;
  isBrazilian?: boolean;
  country?: string;
  street?: string;
  number?: string;
  complement?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  neighborhood?: string;
  medicalHistory?: string;
  allergies?: string;
  medications?: string;
  bloodType?: string;
  height?: number;
  weight?: number;
  imc?: number;
  emergencyContact?: string;
  initialChecklistCompletedAt?: string;
  initialChecklistVersion?: string;
  createdAt: string;
  updatedAt: string;
  user?: {
    id: string;
    email: string;
    name: string;
    /** Status do usuário: 'active' | 'suspended' */
    status?: 'active' | 'suspended';
  };
}

export interface CreatePatientDTO {
  userId: string;
  phone?: string;
  cpf?: string;
  dateOfBirth?: string;
  gender?: 'M' | 'F' | 'Outro';
  isBrazilian?: boolean;
  country?: string;
  street?: string;
  number?: string;
  complement?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  neighborhood?: string;
  medicalHistory?: string;
  allergies?: string;
  medications?: string;
}

export interface UpdatePatientDTO {
  phone?: string;
  cpf?: string;
  dateOfBirth?: string;
  gender?: 'M' | 'F' | 'Outro' | string;
  birthPlace?: string;
  isBrazilian?: boolean;
  country?: string;
  street?: string;
  number?: string;
  complement?: string;
  neighborhood?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  medicalHistory?: string;
  allergies?: string;
  medications?: string;
  bloodType?: string;
  height?: number;
  weight?: number;
  emergencyContact?: string;
}

export interface UpdateAddressDTO {
  street?: string;
  number?: string;
  complement?: string;
  neighborhood?: string;
  city?: string;
  state?: string;
  zipCode?: string;
}

export interface MedicalHistoryDTO {
  medicalHistory?: string;
  allergies?: string | string[];
  medications?: string | string[];
}

export interface CompleteInitialChecklistDTO {
  phone?: string;
  cpf?: string;
  dateOfBirth?: string;
  gender?: 'M' | 'F' | 'Outro';
  birthPlace?: string;
  isBrazilian?: boolean;
  country?: string;
  street?: string;
  number?: string;
  complement?: string;
  neighborhood?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  medicalHistory?: string;
  allergies?: string;
  medications?: string;
  bloodType?: string;
  height?: number;
  weight?: number;
  checklistVersion?: string;
}

export interface InitialChecklistStatusDTO {
  shouldShowChecklist: boolean;
  completedAt?: string;
  checklistVersion?: string;
  patient: PatientDTO;
}

/**
 * List all patients
 */
export async function listPatients(
  search?: string,
  status?: 'active' | 'suspended',
): Promise<ApiResponse<PatientDTO[]>> {
  const params = new URLSearchParams();
  if (search) params.set('search', search);
  if (status) params.set('status', status);
  const qs = params.toString();
  const url = qs ? `${API_ENDPOINTS.PATIENTS.LIST}?${qs}` : API_ENDPOINTS.PATIENTS.LIST;
  return apiClient.get(url);
}

/**
 * Get patient by ID
 */
export async function getPatientById(id: string): Promise<ApiResponse<PatientDTO>> {
  return apiClient.get(API_ENDPOINTS.PATIENTS.GET(id));
}

/**
 * Resolve perfil do paciente a partir do ID de appointment.
 * No Dr. House, `appointment.patientId` costuma ser o **userId** (não o id da tabela patients).
 * Tenta perfil por id e, se não achar, por userId.
 */
export async function resolvePatientProfile(
  patientIdOrUserId: string,
): Promise<ApiResponse<PatientDTO>> {
  const byId = await getPatientById(patientIdOrUserId);
  if (byId.success && byId.data) {
    return byId;
  }

  const byUser = await getPatientByUserId(patientIdOrUserId);
  if (byUser.success && byUser.data) {
    return byUser;
  }

  return {
    success: false,
    error:
      (typeof byUser.error === 'string' && byUser.error) ||
      (typeof byId.error === 'string' && byId.error) ||
      'Paciente não encontrado',
    status: byUser.status || byId.status,
  };
}

/**
 * Get patient by user ID
 */
export async function getPatientByUserId(
  userId: string,
): Promise<ApiResponse<PatientDTO>> {
  console.log('[PatientsService] getPatientByUserId called with userId:', userId);
  const response = await apiClient.get(API_ENDPOINTS.PATIENTS.GET_BY_USER(userId));
  console.log('[PatientsService] getPatientByUserId response:', response);
  if (response.data?.id) {
    console.log('[PatientsService] ✓ Patient ID:', response.data.id);
  }
  return response;
}

/**
 * Create patient profile
 */
export async function createPatient(
  data: CreatePatientDTO,
): Promise<ApiResponse<PatientDTO>> {
  return apiClient.post(API_ENDPOINTS.PATIENTS.CREATE, data);
}

/**
 * Update patient profile
 */
export async function updatePatient(
  id: string,
  data: UpdatePatientDTO,
): Promise<ApiResponse<PatientDTO>> {
  return apiClient.put(API_ENDPOINTS.PATIENTS.UPDATE(id), data);
}

/**
 * Update patient address
 */
export async function updatePatientAddress(
  id: string,
  data: UpdateAddressDTO,
): Promise<ApiResponse<PatientDTO>> {
  return apiClient.put(API_ENDPOINTS.PATIENTS.UPDATE_ADDRESS(id), data);
}

/**
 * Get patient medical history
 */
export async function getPatientMedicalHistory(
  patientId: string,
): Promise<ApiResponse<MedicalHistoryDTO>> {
  return apiClient.get(API_ENDPOINTS.PATIENTS.GET_MEDICAL_HISTORY(patientId));
}

export async function getInitialChecklistStatus(): Promise<ApiResponse<InitialChecklistStatusDTO>> {
  return apiClient.get(API_ENDPOINTS.PATIENTS.INITIAL_CHECKLIST_STATUS);
}

export async function completeInitialChecklist(
  data: CompleteInitialChecklistDTO,
): Promise<ApiResponse<{ patient: PatientDTO; completedAt?: string; checklistVersion?: string }>> {
  return apiClient.post(API_ENDPOINTS.PATIENTS.COMPLETE_INITIAL_CHECKLIST, data);
}
